import { BadgeVenta, VENTA_META } from '@/components/BadgeVenta';
import { useCamaraFotos } from '@/components/fotos/CamaraFotos';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  useActualizarSemana,
  useCierrePreview,
  useFijarLineaBase,
  useSemanaActual,
} from '@/hooks/useCierre';
import {
  useAvanzarSprint,
  useObrasEstados,
  useRetrocederSprint,
  useSetEstadoObra,
  useSprintsCatalogo,
} from '@/hooks/useObras';
import { type ObraEstadoLite, cliente, esGestion as esAdminFn } from '@/lib/cliente';
import { cn } from '@/lib/utils';
import type {
  AvanceSubPartida,
  CierrePreview,
  EstadoVenta,
  HitoDesembolsoCodigo,
  PreviewObra,
  PreviewSubPartida,
  RegistrarAvance,
  RespuestaApi,
} from '@adelante/shared';
import { HITOS_DESEMBOLSO } from '@adelante/shared';
import {
  ArrowCircleLeft,
  ArrowCircleRight,
  ArrowUUpLeft,
  Buildings,
  Camera,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  Check,
  CheckCircle,
  CircleNotch,
  CurrencyDollar,
  Flag,
  FlagPennant,
  Images,
  MagnifyingGlass,
  Pause,
  Percent,
  Play,
  WarningCircle,
  X,
  XCircle,
} from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AvanceRapidoDialog } from './AvanceRapidoDialog';
import { CongelarObraDialog } from './CongelarObraDialog';
import { ControlCalidadFila, bloqueoCalidad, useControlesCalidad } from './ControlCalidadFila';
import { RegistrarNCDialog } from './RegistrarNCDialog';

/**
 * Vista Kanban — tablero por sprint con captura inline.
 *
 * - Muestra todas las obras habilitadas (en_ejecucion y en_espera) sin filtrar
 *   por proyecto; las congeladas aparecen grises con un botón Play para
 *   reactivar.
 * - Click en tarjeta → se expande inline mostrando sub-partidas con botones
 *   ✓ (marca completa) · ✗ (abre NC) · % (abre modal con presets).
 * - Acciones de sprint en la tarjeta expandida: Atrás (sólo si el sprint
 *   anterior es de espera), Congelar (abre modal de causa), Avanzar (sólo si
 *   todas las críticas están al 100%; el backend también valida).
 */

interface SubCtx {
  codigo: string;
  sprint: number;
  sub: PreviewSubPartida;
}

export function KanbanAvanceVista({ proyecto }: { proyecto: string | null }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useCierrePreview();
  const semanaQ = useSemanaActual();
  const navigate = useNavigate();
  const esAdmin = esAdminFn();

  const [expandida, setExpandida] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [ventaFiltro, setVentaFiltro] = useState<EstadoVenta | null>(null);
  const [ncDe, setNcDe] = useState<SubCtx | null>(null);
  const [pctDe, setPctDe] = useState<SubCtx | null>(null);
  const [congelarDe, setCongelarDe] = useState<string | null>(null);
  // Estado de drag-and-drop entre columnas.
  const [dragging, setDragging] = useState<{
    codigo: string;
    actual: number;
    siguiente: number | null;
    anterior: number | null;
    puedeRetroceder: boolean;
  } | null>(null);

  const avanzar = useAvanzarSprint();
  const retroceder = useRetrocederSprint();
  const setEstado = useSetEstadoObra();
  const fijarLinea = useFijarLineaBase();
  const actualizarSemana = useActualizarSemana();
  // Update OPTIMISTA: el cambio se ve al instante en el tablero (no espera el
  // PUT ni el refetch). El guardado va por detrás; si falla, se revierte.
  const registrar = useMutation({
    mutationFn: ({ codigo, body }: { codigo: string; body: RegistrarAvance }) =>
      cliente.obras.registrarAvance(codigo, body),
    onMutate: async ({ codigo, body }) => {
      await qc.cancelQueries({ queryKey: ['cierre', 'preview'] });
      const prev = qc.getQueryData<RespuestaApi<CierrePreview>>(['cierre', 'preview']);
      qc.setQueryData<RespuestaApi<CierrePreview>>(['cierre', 'preview'], (old) =>
        aplicarOptimistaPreview(old, codigo, body),
      );
      return { prev };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(['cierre', 'preview'], ctx.prev);
      toast.error('No se pudo guardar', { description: e instanceof Error ? e.message : 'Error' });
    },
    onSettled: () => {
      // El update ya es OPTIMISTA: marcamos stale SIN refetch inmediato
      // (refetchType: 'none'). Cada captura disparaba un refetch del preview
      // (~374 kB) + avance + matriz, saturando el pool de Azure SQL y volviendo
      // todo lento. Se re-sincroniza al navegar (las queries quedan stale).
      qc.invalidateQueries({ queryKey: ['cierre'], refetchType: 'none' });
      qc.invalidateQueries({ queryKey: ['avance'], refetchType: 'none' });
      qc.invalidateQueries({ queryKey: ['matriz'], refetchType: 'none' });
    },
  });

  // Reordenar la prioridad de las tarjetas dentro de un sprint (botones ↑↓).
  // Optimista: el orden se ve al instante; el PUT guarda por detrás.
  const reordenar = useMutation({
    mutationFn: (codigos: string[]) => cliente.obras.reordenar(codigos),
    onMutate: async (codigos) => {
      await qc.cancelQueries({ queryKey: ['cierre', 'preview'] });
      const prev = qc.getQueryData<RespuestaApi<CierrePreview>>(['cierre', 'preview']);
      qc.setQueryData<RespuestaApi<CierrePreview>>(['cierre', 'preview'], (old) =>
        aplicarOrdenOptimista(old, codigos),
      );
      return { prev };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(['cierre', 'preview'], ctx.prev);
      toast.error('No se pudo reordenar', {
        description: e instanceof Error ? e.message : 'Error',
      });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['cierre'], refetchType: 'none' });
    },
  });

  const semana = semanaQ.data?.data ?? null;
  const lineaBaseFijada = (data?.data?.obras ?? []).some((o) => o.sprint_plan != null);

  // Filtro: por proyecto (prefijo "VB-") salvo "Todos" (proyecto=null) + texto.
  const obras = useMemo(() => {
    let lista = data?.data?.obras ?? [];
    if (proyecto) lista = lista.filter((o) => o.codigo.startsWith(`${proyecto}-`));
    if (ventaFiltro) lista = lista.filter((o) => o.estado_venta === ventaFiltro);
    const q = busqueda.trim().toLowerCase();
    if (q) lista = lista.filter((o) => `${o.codigo} ${o.tipo_casa}`.toLowerCase().includes(q));
    return lista;
  }, [data, proyecto, busqueda, ventaFiltro]);

  // Columnas extremo (Por Iniciar / Terminadas). Vienen del listado completo de
  // obras, NO del preview (que solo trae en_ejecucion/en_espera). Se filtran
  // por proyecto + búsqueda igual que el resto del tablero.
  const obrasListaQ = useObrasEstados();
  const { porIniciar, terminadas } = useMemo(() => {
    const lista = obrasListaQ.data?.data ?? [];
    const q = busqueda.trim().toLowerCase();
    const pasa = (o: ObraEstadoLite) => {
      if (proyecto && !o.codigo.startsWith(`${proyecto}-`)) return false;
      if (ventaFiltro && o.estado_venta !== ventaFiltro) return false;
      if (q && !`${o.codigo} ${o.tipo_casa ?? ''}`.toLowerCase().includes(q)) return false;
      return true;
    };
    const ordenar = (a: ObraEstadoLite, b: ObraEstadoLite) => a.codigo.localeCompare(b.codigo);
    return {
      porIniciar: lista.filter((o) => o.estado === 'pendiente' && pasa(o)).sort(ordenar),
      terminadas: lista.filter((o) => o.estado === 'finalizada' && pasa(o)).sort(ordenar),
    };
  }, [obrasListaQ.data, proyecto, busqueda, ventaFiltro]);

  // Conteo por estado de venta (respeta proyecto + búsqueda, NO el filtro de
  // venta) para mostrar cuántas obras hay de cada tipo en los chips de arriba.
  const ventaConteos = useMemo(() => {
    const lista = obrasListaQ.data?.data ?? [];
    const q = busqueda.trim().toLowerCase();
    const conteos: Record<EstadoVenta, number> = {
      formalizada: 0,
      reservada: 0,
      disponible: 0,
      entregada: 0,
    };
    let total = 0;
    for (const o of lista) {
      if (proyecto && !o.codigo.startsWith(`${proyecto}-`)) continue;
      if (q && !`${o.codigo} ${o.tipo_casa ?? ''}`.toLowerCase().includes(q)) continue;
      // Solo el TABLERO (en ejecución + congeladas), igual que el reporte de venta.
      // Por Iniciar (pendientes) y Terminadas (finalizadas) se ven en sus columnas
      // pero NO suman en los chips.
      if (o.estado !== 'en_ejecucion' && o.estado !== 'en_espera') continue;
      total++;
      if (o.estado_venta) conteos[o.estado_venta]++;
    }
    return { conteos, total };
  }, [obrasListaQ.data, proyecto, busqueda]);

  // Scroll horizontal de los sprints con botones (para pantallas no táctiles).
  const scrollerRef = useRef<HTMLDivElement>(null);
  const desplazarSprints = (dir: number) =>
    scrollerRef.current?.scrollBy({ left: dir * 360, behavior: 'smooth' });

  // Catálogo de sprints → nombre real + nº de críticas para los encabezados.
  const catalogoQ = useSprintsCatalogo();
  const catalogoSprint = useMemo(() => {
    const m = new Map<
      number,
      {
        nombre: string;
        criticas: number;
        es_espera: boolean;
        hitoDesembolso: HitoDesembolsoCodigo | null;
      }
    >();
    for (const s of catalogoQ.data?.data ?? []) {
      m.set(s.numero_global, {
        nombre: s.nombre,
        criticas: s.criticas,
        es_espera: s.es_espera,
        hitoDesembolso: s.hito_desembolso,
      });
    }
    return m;
  }, [catalogoQ.data]);

  // Pendientes arrastrados por sprint de ORIGEN (la columna donde se muestran),
  // y dentro por obra. Se arman desde las obras del preview ya filtradas,
  // tomando solo las arrastradas aún incompletas.
  const arrastradosPorSprint = useMemo(() => {
    const m = new Map<number, { codigo: string; subs: PreviewSubPartida[] }[]>();
    for (const o of obras) {
      // No se filtran las completadas: deben seguir visibles (✓) hasta el cierre
      // para poder revertir una marcada por error. El backend ya excluye las que
      // un cierre fijó a 100%.
      const pend = o.arrastradas;
      if (pend.length === 0) continue;
      const porSprint = new Map<number, PreviewSubPartida[]>();
      for (const s of pend) {
        const arr = porSprint.get(s.sprint_numero) ?? [];
        arr.push(s);
        porSprint.set(s.sprint_numero, arr);
      }
      for (const [sprint, subs] of porSprint) {
        const lista = m.get(sprint) ?? [];
        lista.push({ codigo: o.codigo, subs });
        m.set(sprint, lista);
      }
    }
    return m;
  }, [obras]);

  const columnas = useMemo(() => {
    const map = new Map<number, PreviewObra[]>();
    for (const o of obras) {
      const arr = map.get(o.sprint_actual) ?? [];
      arr.push(o);
      map.set(o.sprint_actual, arr);
    }
    // Incluir columnas para sprints que SOLO tienen arrastrados (sin obras en
    // ese sprint). Importante al buscar una obra: sus pendientes de sprints
    // previos viven en columnas donde la obra ya no está parada.
    for (const sprint of arrastradosPorSprint.keys()) {
      if (!map.has(sprint)) map.set(sprint, []);
    }
    return Array.from(map.entries())
      .map(([sprint, list]) => ({
        sprint,
        // Orden manual (↑↓) primero; las sin orden van al final, desempate por código.
        obras: list.sort(compararOrden),
        // "Listas" cuenta solo las en ejecución que ya cumplen todas las críticas.
        listas: list.filter((o) => o.estado === 'en_ejecucion' && o.va_a_avanzar).length,
      }))
      .sort((a, b) => a.sprint - b.sprint);
  }, [obras, arrastradosPorSprint]);

  // No re-lanza: muestra el toast y devuelve null al fallar. Evita el
  // "unhandled promise rejection" cuando se invoca sin await (drag-and-drop).
  async function correr<T>(p: Promise<T>, ok: string, err = 'No se pudo aplicar la acción') {
    try {
      const r = await p;
      toast.success(ok);
      return r;
    } catch (e) {
      toast.error(err, { description: e instanceof Error ? e.message : 'Error' });
      return null;
    }
  }

  /** Soltar una tarjeta en una columna: avanzar / retroceder / rechazar. */
  function handleDropOnColumn(targetSprint: number) {
    if (!dragging) return;
    const d = dragging;
    setDragging(null);
    if (targetSprint === d.actual) return; // mismo sprint = no-op
    if (d.siguiente !== null && targetSprint === d.siguiente) {
      correr(avanzar.mutateAsync(d.codigo), `${d.codigo}: avanzada a S${targetSprint}`);
      return;
    }
    if (d.puedeRetroceder && d.anterior !== null && targetSprint === d.anterior) {
      correr(retroceder.mutateAsync(d.codigo), `${d.codigo}: retrocedida a S${targetSprint}`);
      return;
    }
    toast.warning(
      'Solo se puede mover al sprint siguiente, o volver al anterior si no fue cerrado.',
    );
  }

  async function handleFijarLinea() {
    if (!semana) return;
    // Re-fijar cambia la BASE DE CÁLCULO del reporte (la foto de inicio de
    // semana): el "logrado de la semana" pasa a medirse desde el estado actual.
    // Confirmar para no pisarla sin querer. (La primera vez no pregunta.)
    if (lineaBaseFijada) {
      const ok = window.confirm(
        'Estás por RE-FIJAR la línea base.\n\nEsto cambia la base de cálculo de los reportes: el avance de la semana (% Sem, m² Sem, ₡ Sem) se medirá desde el estado ACTUAL, no desde la foto anterior.\n\n¿Deseás continuar?',
      );
      if (!ok) return;
    }
    try {
      const r = await fijarLinea.mutateAsync(Number(semana.id));
      if (r.data) toast.success(`Línea base fijada · ${r.data.fijadas} obras`);
    } catch (e) {
      toast.error('No se pudo fijar la línea base', {
        description: e instanceof Error ? e.message : 'Error',
      });
    }
  }

  async function handleDias(valor: number) {
    if (!semana || valor === semana.dias_efectivos) return;
    try {
      await actualizarSemana.mutateAsync({
        semanaId: Number(semana.id),
        body: { dias_efectivos: valor },
      });
      toast.success(`Días efectivos: ${valor}`);
    } catch (e) {
      toast.error('No se pudo actualizar', {
        description: e instanceof Error ? e.message : 'Error',
      });
    }
  }

  // Las acciones de sprint sí esperan; la captura (% ✗ ✓) es optimista y no se
  // bloquea entre sí, para poder marcar varias sub-partidas seguidas.
  const enviando = avanzar.isPending || retroceder.isPending || setEstado.isPending;

  return (
    <div className="space-y-3">
      {/* Barra superior: semana + búsqueda + acciones (siempre visible) */}
      <div className="flex flex-wrap items-center gap-2">
        {semana && (
          <Badge variant="outline" className="shrink-0">
            Semana {semana.numero_semana} · {semana.anio}
          </Badge>
        )}
        <div className="relative min-w-[160px] flex-1">
          <MagnifyingGlass
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            weight="bold"
            aria-hidden="true"
          />
          <Input
            type="search"
            inputMode="search"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar obra…"
            className="h-8 pl-8 pr-8"
            aria-label="Buscar obra"
          />
          {busqueda && (
            <button
              type="button"
              onClick={() => setBusqueda('')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-zinc-100"
              aria-label="Limpiar"
            >
              <X className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
            </button>
          )}
        </div>
        {semana && esAdmin && (
          <div className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
            <span>Días ef.</span>
            <Input
              type="number"
              min={1}
              max={7}
              defaultValue={semana.dias_efectivos}
              key={semana.dias_efectivos}
              onBlur={(e) => handleDias(Number(e.target.value))}
              className="h-8 w-14 text-center"
              aria-label="Días efectivos"
            />
          </div>
        )}
        {semana && esAdmin && (
          <Button
            variant={lineaBaseFijada ? 'outline' : 'default'}
            size="sm"
            onClick={handleFijarLinea}
            disabled={fijarLinea.isPending}
            className={cn(
              'shrink-0',
              !lineaBaseFijada && 'bg-amber-500 text-white hover:bg-amber-600',
            )}
          >
            {fijarLinea.isPending ? (
              <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
            ) : (
              <Flag className="h-4 w-4" weight="bold" aria-hidden="true" />
            )}
            {lineaBaseFijada ? 'Re-fijar' : 'Fijar línea base'}
          </Button>
        )}
      </div>

      {/* Filtro por estado de venta (F/R/D/E) */}
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => setVentaFiltro(null)}
          className={cn(
            'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
            ventaFiltro === null
              ? 'border-adelante-primary bg-adelante-primary text-adelante-foreground'
              : 'border-border bg-white text-muted-foreground',
          )}
        >
          Todas
          <span className="ml-1 tabular-nums opacity-70">{ventaConteos.total}</span>
        </button>
        {(['formalizada', 'reservada', 'disponible', 'entregada'] as const).map((ev) => {
          const m = VENTA_META[ev];
          const activo = ventaFiltro === ev;
          return (
            <button
              key={ev}
              type="button"
              onClick={() => setVentaFiltro(activo ? null : ev)}
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                activo ? m.chip : 'border-border bg-white text-muted-foreground',
              )}
            >
              <span className={cn('inline-block h-2 w-2 rounded-full', m.dot)} aria-hidden="true" />
              {m.label}
              <span className="tabular-nums opacity-70">{ventaConteos.conteos[ev]}</span>
            </button>
          );
        })}
      </div>

      {/* Cuerpo */}
      {isLoading ? (
        <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
          <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
          Cargando tablero…
        </div>
      ) : isError ? (
        <div className="space-y-2 py-8 text-center text-sm text-muted-foreground">
          <p>No se pudo cargar el tablero.</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="text-adelante-primary-dark underline"
          >
            Reintentar
          </button>
        </div>
      ) : obras.length === 0 && porIniciar.length === 0 && terminadas.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 p-6 text-center">
          <WarningCircle className="h-7 w-7 text-amber-500" weight="fill" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">
            {busqueda ? `Ninguna obra coincide con «${busqueda}».` : 'No hay obras habilitadas.'}
          </p>
        </Card>
      ) : (
        <>
          {/* Regla: no se captura ni se mueve hasta fijar la línea base. */}
          {semana && !lineaBaseFijada && (
            <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <Flag className="h-4 w-4 shrink-0" weight="fill" aria-hidden="true" />
              <span>
                Fijá la <strong>línea base</strong> de la semana antes de capturar avances o mover
                tarjetas. {esAdmin ? 'Usá el botón de arriba.' : 'Pedíselo a un administrador.'}
              </span>
            </div>
          )}
          <div className="relative left-1/2 w-screen -translate-x-1/2">
            <button
              type="button"
              onClick={() => desplazarSprints(-1)}
              aria-label="Sprints anteriores"
              className="absolute left-2 top-1/2 z-20 hidden -translate-y-1/2 rounded-full border border-border bg-white/95 p-1.5 shadow-md hover:bg-white md:block"
            >
              <CaretLeft className="h-5 w-5" weight="bold" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => desplazarSprints(1)}
              aria-label="Sprints siguientes"
              className="absolute right-2 top-1/2 z-20 hidden -translate-y-1/2 rounded-full border border-border bg-white/95 p-1.5 shadow-md hover:bg-white md:block"
            >
              <CaretRight className="h-5 w-5" weight="bold" aria-hidden="true" />
            </button>
            <div ref={scrollerRef} className="overflow-x-auto px-4 pb-4">
              <div className="flex min-w-max gap-3">
                {/* Columna extremo izquierda: obras configuradas sin arrancar.
                  Iniciar NO depende de la línea base: la obra debe estar en
                  ejecución ANTES de poder fijar su plan. */}
                <ColumnaExtremo
                  tipo="por-iniciar"
                  obras={porIniciar}
                  enviando={enviando}
                  bloqueado={false}
                  onAccion={(c) =>
                    correr(
                      setEstado.mutateAsync({ codigo: c, estado: 'en_ejecucion' }),
                      `${c}: iniciada`,
                    )
                  }
                />
                {columnas.map((col) => (
                  <ColumnaSprint
                    key={col.sprint}
                    sprint={col.sprint}
                    nombre={catalogoSprint.get(col.sprint)?.nombre ?? null}
                    criticas={catalogoSprint.get(col.sprint)?.criticas ?? null}
                    esEspera={catalogoSprint.get(col.sprint)?.es_espera ?? false}
                    hitoDesembolso={catalogoSprint.get(col.sprint)?.hitoDesembolso ?? null}
                    obras={col.obras}
                    arrastrados={arrastradosPorSprint.get(col.sprint) ?? []}
                    listas={col.listas}
                    expandida={expandida}
                    dragging={dragging}
                    bloqueado={!lineaBaseFijada}
                    onDrop={handleDropOnColumn}
                    onReordenar={(codigos) => reordenar.mutate(codigos)}
                    enviando={enviando}
                    onToggle={(c) => setExpandida((e) => (e === c ? null : c))}
                    onDragStart={(o) =>
                      setDragging({
                        codigo: o.codigo,
                        actual: o.sprint_actual,
                        siguiente: o.sprint_siguiente,
                        anterior: o.sprint_anterior,
                        puedeRetroceder: puedeRetrocederObra(o),
                      })
                    }
                    onDragEnd={() => setDragging(null)}
                    onCapturar={(c) => navigate(`/campo/avance/${encodeURIComponent(c)}`)}
                    onAvanzar={(c) =>
                      correr(avanzar.mutateAsync(c), `${c}: avanzada al siguiente sprint`)
                    }
                    onTerminar={(c) =>
                      correr(
                        setEstado.mutateAsync({ codigo: c, estado: 'finalizada' }),
                        `${c}: terminada`,
                        'No se pudo terminar la obra',
                      )
                    }
                    onRetroceder={(c) => correr(retroceder.mutateAsync(c), `${c}: retrocedida`)}
                    onCongelarPedir={(c) => setCongelarDe(c)}
                    onDescongelar={(c) =>
                      correr(
                        setEstado.mutateAsync({ codigo: c, estado: 'en_ejecucion' }),
                        `${c}: reactivada`,
                      )
                    }
                    onMarcarCompleta={(c, sub) => {
                      // Toggle: si ya está completa → revierte al piso (% del
                      // último corte cerrado; no se puede bajar de ahí). Si no →
                      // completa al 100% + limpia NC. Optimista (sin await).
                      const estaCompleta = sub.completada || sub.pct_completado >= 100;
                      registrar.mutate({
                        codigo: c,
                        body: estaCompleta
                          ? {
                              sub_partida_id: sub.sub_partida_id,
                              pct_completado: sub.piso_pct,
                              completada: sub.piso_pct >= 100,
                            }
                          : {
                              sub_partida_id: sub.sub_partida_id,
                              pct_completado: 100,
                              completada: true,
                              nc_causa: null,
                            },
                      });
                    }}
                    onPedirNC={(c, sub, sprint) => setNcDe({ codigo: c, sprint, sub })}
                    onPedirPct={(c, sub, sprint) => setPctDe({ codigo: c, sprint, sub })}
                  />
                ))}
                {/* Columna extremo derecha: obras finalizadas (automático en cierre). */}
                <ColumnaExtremo
                  tipo="terminadas"
                  obras={terminadas}
                  enviando={enviando}
                  bloqueado={false}
                  onAccion={(c) =>
                    correr(
                      setEstado.mutateAsync({ codigo: c, estado: 'en_ejecucion' }),
                      `${c}: devuelta a ejecución`,
                    )
                  }
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Diálogo: congelar obra (NC inactividad) */}
      <CongelarObraDialog
        codigo={congelarDe}
        pending={setEstado.isPending}
        onClose={() => setCongelarDe(null)}
        onConfirmar={async (causa, nota) => {
          if (!congelarDe) return;
          try {
            await setEstado.mutateAsync({
              codigo: congelarDe,
              estado: 'en_espera',
              motivo: nota ? `${causa} — ${nota}` : causa,
            });
            toast.success(`${congelarDe}: congelada`);
            setCongelarDe(null);
            setExpandida(null);
          } catch (e) {
            toast.error('No se pudo congelar', {
              description: e instanceof Error ? e.message : 'Error',
            });
          }
        }}
      />

      {/* Diálogo: NC de sub-partida (causas aplica_nc) */}
      <RegistrarNCDialog
        subPartida={ncDe ? previewToAvance(ncDe.sub, ncDe.sprint) : null}
        pending={registrar.isPending}
        onClose={() => setNcDe(null)}
        onConfirmar={(causaCodigo, nota) => {
          if (!ncDe) return;
          // NC solo: NO toca el %. Optimista (sin await) → cierra al instante.
          registrar.mutate({
            codigo: ncDe.codigo,
            body: { sub_partida_id: ncDe.sub.sub_partida_id, nc_causa: causaCodigo, nc_nota: nota },
          });
          setNcDe(null);
        }}
      />

      {/* Diálogo: % rápido sin navegar */}
      <AvanceRapidoDialog
        sub={
          pctDe
            ? {
                sub_partida_id: pctDe.sub.sub_partida_id,
                codigo: pctDe.sub.codigo,
                nombre: pctDe.sub.nombre,
                pct_completado: pctDe.sub.pct_completado,
              }
            : null
        }
        pending={registrar.isPending}
        onClose={() => setPctDe(null)}
        onConfirmar={(pct) => {
          if (!pctDe) return;
          // % solo: NO toca la NC. Optimista (sin await) → cierra al instante.
          registrar.mutate({
            codigo: pctDe.codigo,
            body: {
              sub_partida_id: pctDe.sub.sub_partida_id,
              pct_completado: pct,
              completada: pct >= 100,
            },
          });
          setPctDe(null);
        }}
      />
    </div>
  );
}

/** Aplica un avance al cache del preview (optimista) y recalcula la obra. */
function aplicarOptimistaPreview(
  old: RespuestaApi<CierrePreview> | undefined,
  codigo: string,
  body: RegistrarAvance,
): RespuestaApi<CierrePreview> | undefined {
  if (!old || old.error !== null || !old.data) return old;
  const tocaPct = body.pct_completado !== undefined || body.completada !== undefined;
  const tocaNc = body.nc_causa !== undefined;
  // Aplica el cambio a una sub-partida (sirve para las del sprint y las arrastradas).
  const actualizar = (s: PreviewSubPartida): PreviewSubPartida => {
    if (s.sub_partida_id !== body.sub_partida_id) return s;
    const next = { ...s };
    if (tocaPct) {
      next.pct_completado = body.completada ? 100 : (body.pct_completado ?? s.pct_completado);
      next.completada = body.completada === true || next.pct_completado >= 100;
    }
    if (tocaNc) next.nc_causa = body.nc_causa ?? null;
    return next;
  };
  const obras = old.data.obras.map((o) => {
    if (o.codigo !== codigo) return o;
    const subs = o.subs.map(actualizar);
    const arrastradas = o.arrastradas.map(actualizar);
    const criticas = subs.filter((s) => s.es_critica);
    const criticasCompletas = criticas.filter((s) => s.completada && s.pct_completado >= 100);
    return {
      ...o,
      subs,
      arrastradas,
      criticas_completas: criticasCompletas.length,
      pendientes_no_completadas: subs.filter((s) => !s.completada).length,
      va_a_avanzar: criticas.length > 0 && criticasCompletas.length === criticas.length,
    };
  });
  return { ...old, data: { ...old.data, obras } };
}

/** Orden de las tarjetas en una columna: `orden` manual asc; sin orden al final. */
function compararOrden(a: PreviewObra, b: PreviewObra): number {
  const oa = a.orden ?? Number.MAX_SAFE_INTEGER;
  const ob = b.orden ?? Number.MAX_SAFE_INTEGER;
  if (oa !== ob) return oa - ob;
  return a.codigo.localeCompare(b.codigo);
}

/** Aplica el nuevo orden (índice en `codigos`) al cache del preview, optimista. */
function aplicarOrdenOptimista(
  old: RespuestaApi<CierrePreview> | undefined,
  codigos: string[],
): RespuestaApi<CierrePreview> | undefined {
  if (!old || old.error !== null || !old.data) return old;
  const pos = new Map(codigos.map((c, i) => [c, i]));
  const obras = old.data.obras.map((o) =>
    pos.has(o.codigo) ? { ...o, orden: pos.get(o.codigo) ?? null } : o,
  );
  return { ...old, data: { ...old.data, obras } };
}

/**
 * Se puede retroceder al sprint anterior si NO está cerrado (fue un avance
 * manual de esta semana, reversible) o si es de espera (colado de concreto).
 */
function puedeRetrocederObra(o: PreviewObra): boolean {
  if (o.sprint_anterior != null) {
    return !o.sprint_anterior_cerrado || o.sprint_anterior_es_espera === true;
  }
  // En el primer sprint: "Atrás" = volver a «Por Iniciar». Permitido solo si
  // ninguna sub-partida tiene avance.
  return o.subs.every((s) => s.pct_completado <= 0 && !s.completada);
}

/** Adapta una PreviewSubPartida al shape mínimo que necesita RegistrarNCDialog. */
function previewToAvance(sub: PreviewSubPartida, sprint: number): AvanceSubPartida {
  return {
    sub_partida_id: sub.sub_partida_id,
    codigo: sub.codigo,
    nombre: sub.nombre,
    partida_id: 0,
    partida_codigo: '',
    partida_nombre: '',
    sprint_numero: sprint,
    es_critica: sub.es_critica,
    peso: 0,
    pct_completado: sub.pct_completado,
    completada: sub.completada,
    nc_causa: sub.nc_causa,
    nc_nota: null,
    piso_pct: sub.piso_pct,
    arrastrada: false,
  };
}

// ===========================================================================
// Columna
// ===========================================================================

interface ColumnaProps {
  sprint: number;
  /** Nombre real del sprint (LOSA, ESPERA MUROS N1…). null = usar "Sprint N". */
  nombre: string | null;
  /** Nº de sub-partidas críticas del sprint (null = desconocido). */
  criticas: number | null;
  /** true si es sprint de espera. */
  esEspera: boolean;
  /** Hito de desembolso que dispara este sprint al completarse (null = ninguno). */
  hitoDesembolso: HitoDesembolsoCodigo | null;
  obras: PreviewObra[];
  /** Sub-partidas arrastradas de otras obras cuyo sprint de origen es esta columna. */
  arrastrados: { codigo: string; subs: PreviewSubPartida[] }[];
  listas: number;
  expandida: string | null;
  dragging: {
    codigo: string;
    actual: number;
    siguiente: number | null;
    anterior: number | null;
    puedeRetroceder: boolean;
  } | null;
  enviando: boolean;
  /** true = no se permite capturar ni mover (línea base no fijada). */
  bloqueado: boolean;
  onToggle: (codigo: string) => void;
  onDrop: (targetSprint: number) => void;
  /** Persiste el nuevo orden (prioridad) de las obras de esta columna. */
  onReordenar: (codigos: string[]) => void;
  onDragStart: (obra: PreviewObra) => void;
  onDragEnd: () => void;
  onCapturar: (codigo: string) => void;
  onAvanzar: (codigo: string) => void;
  onTerminar: (codigo: string) => void;
  onRetroceder: (codigo: string) => void;
  onCongelarPedir: (codigo: string) => void;
  onDescongelar: (codigo: string) => void;
  onMarcarCompleta: (codigo: string, sub: PreviewSubPartida) => void;
  onPedirNC: (codigo: string, sub: PreviewSubPartida, sprint: number) => void;
  onPedirPct: (codigo: string, sub: PreviewSubPartida, sprint: number) => void;
}

function ColumnaSprint(p: ColumnaProps) {
  const pct = p.obras.length > 0 ? Math.round((p.listas / p.obras.length) * 100) : 0;
  const d = p.dragging;
  const hito = p.hitoDesembolso
    ? HITOS_DESEMBOLSO.find((h) => h.codigo === p.hitoDesembolso)
    : null;

  // Mover la tarjeta en posición i hacia arriba (dir=-1) o abajo (dir=+1):
  // intercambia con su vecina y persiste el orden completo de la columna.
  const mover = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= p.obras.length) return;
    const codigos = p.obras.map((o) => o.codigo);
    [codigos[i], codigos[j]] = [codigos[j]!, codigos[i]!];
    p.onReordenar(codigos);
  };
  const esTargetSiguiente = d?.siguiente === p.sprint && d?.actual !== p.sprint;
  const esTargetAnterior =
    d?.puedeRetroceder === true && d.anterior === p.sprint && d.actual !== p.sprint;
  const esOrigen = d?.actual === p.sprint;
  const esInvalido = d && !esTargetSiguiente && !esTargetAnterior && !esOrigen;

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = esTargetSiguiente || esTargetAnterior ? 'move' : 'none';
      }}
      onDrop={(e) => {
        e.preventDefault();
        p.onDrop(p.sprint);
      }}
      className={cn(
        'flex w-[300px] shrink-0 flex-col rounded-md border-2 bg-zinc-50 transition-colors',
        esTargetSiguiente
          ? 'border-adelante-primary bg-adelante-primary/5'
          : esTargetAnterior
            ? 'border-blue-400 bg-blue-50'
            : esInvalido
              ? 'border-zinc-200 opacity-50'
              : 'border-border',
      )}
      style={hito ? { borderTopColor: hito.color, borderTopWidth: 4 } : undefined}
    >
      <div className="border-b border-border bg-white px-3 py-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Sprint {p.sprint}
              {p.esEspera && ' · espera'}
            </p>
            <p className="truncate text-sm font-semibold leading-tight" title={p.nombre ?? ''}>
              {p.nombre ?? `Sprint ${p.sprint}`}
            </p>
            <div className="flex items-center gap-1.5">
              {p.criticas != null && (
                <p className="text-[10px] text-muted-foreground">
                  {p.criticas} {p.criticas === 1 ? 'crítica' : 'críticas'}
                </p>
              )}
              {hito && (
                <span
                  className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white"
                  style={{ backgroundColor: hito.color }}
                  title={`Dispara desembolso: ${hito.nombre}`}
                >
                  <CurrencyDollar className="h-2.5 w-2.5" weight="bold" aria-hidden="true" />
                  {hito.nombre}
                </span>
              )}
            </div>
          </div>
          <span className="shrink-0 text-[11px] text-muted-foreground">
            {p.obras.length} {p.obras.length === 1 ? 'obra' : 'obras'}
          </span>
        </div>
        {p.obras.length > 0 && (
          <>
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-zinc-200">
              <div
                className="h-full rounded-full bg-adelante-primary transition-all"
                style={{ width: `${pct}%` }}
                aria-hidden="true"
              />
            </div>
            <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
              {p.listas} listas{p.listas > 0 && ` · ${pct}%`}
            </p>
          </>
        )}
      </div>
      <div className="flex-1 space-y-2 p-2">
        {p.obras.map((o, i) => (
          <TarjetaObra
            key={o.codigo}
            obra={o}
            expandida={p.expandida === o.codigo}
            arrastrando={p.dragging?.codigo === o.codigo}
            enviando={p.enviando}
            bloqueado={p.bloqueado}
            esEspera={p.esEspera}
            puedeSubir={i > 0}
            puedeBajar={i < p.obras.length - 1}
            onSubir={() => mover(i, -1)}
            onBajar={() => mover(i, 1)}
            onDragStart={() => p.onDragStart(o)}
            onDragEnd={p.onDragEnd}
            onToggle={() => p.onToggle(o.codigo)}
            onCapturar={() => p.onCapturar(o.codigo)}
            onAvanzar={() => p.onAvanzar(o.codigo)}
            onTerminar={() => p.onTerminar(o.codigo)}
            onRetroceder={() => p.onRetroceder(o.codigo)}
            onCongelarPedir={() => p.onCongelarPedir(o.codigo)}
            onDescongelar={() => p.onDescongelar(o.codigo)}
            onMarcarCompleta={(sub) => p.onMarcarCompleta(o.codigo, sub)}
            onPedirNC={(sub) => p.onPedirNC(o.codigo, sub, p.sprint)}
            onPedirPct={(sub) => p.onPedirPct(o.codigo, sub, p.sprint)}
          />
        ))}

        {/* Pendientes arrastrados: sub-partidas de este sprint que otras obras
            (ya en sprints posteriores) dejaron sin completar. */}
        {p.arrastrados.length > 0 && (
          <div className="space-y-2 border-t border-dashed border-amber-300 pt-2">
            <p className="flex items-center gap-1 px-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
              <WarningCircle className="h-3.5 w-3.5" weight="fill" aria-hidden="true" />
              Pendientes arrastrados
            </p>
            {p.arrastrados.map((grupo) => (
              <div
                key={grupo.codigo}
                className="rounded-md border border-amber-200 bg-amber-50/60 p-1.5"
              >
                <p className="mb-1 font-mono text-[11px] font-semibold text-amber-900">
                  {grupo.codigo}
                </p>
                <ListaSubs
                  titulo=""
                  subs={grupo.subs}
                  acento="normal"
                  enviando={p.enviando || p.bloqueado}
                  onCompleta={(sub) => p.onMarcarCompleta(grupo.codigo, sub)}
                  onNC={(sub) => p.onPedirNC(grupo.codigo, sub, sub.sprint_numero)}
                  onPct={(sub) => p.onPedirPct(grupo.codigo, sub, sub.sprint_numero)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Columnas extremo: Por Iniciar (izquierda) y Terminadas (derecha)
// ===========================================================================

interface ColumnaExtremoProps {
  tipo: 'por-iniciar' | 'terminadas';
  obras: ObraEstadoLite[];
  enviando: boolean;
  /** true = no se permite iniciar (línea base no fijada). Solo Por Iniciar. */
  bloqueado: boolean;
  onAccion: (codigo: string) => void;
}

/**
 * Columna "ligera" de los extremos del tablero. No es destino de drag:
 *  - Por Iniciar: obras en estado 'pendiente' (admin las configuró sin
 *    arrancar). Botón "Iniciar → Sprint N" las pasa a en_ejecucion.
 *  - Terminadas: obras 'finalizada' (automático al cerrar el último sprint).
 *    Botón "Devolver" las regresa a en_ejecucion.
 */
function ColumnaExtremo(p: ColumnaExtremoProps) {
  const esPorIniciar = p.tipo === 'por-iniciar';
  const Icono = esPorIniciar ? FlagPennant : CheckCircle;
  return (
    <div
      className={cn(
        'flex w-[260px] shrink-0 flex-col rounded-md border-2 border-dashed bg-zinc-50',
        esPorIniciar ? 'border-sky-300' : 'border-emerald-300',
      )}
    >
      <div className="border-b border-border bg-white px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <p className="flex items-center gap-1.5 text-sm font-semibold">
            <Icono
              className={cn('h-4 w-4', esPorIniciar ? 'text-sky-600' : 'text-emerald-600')}
              weight="fill"
              aria-hidden="true"
            />
            {esPorIniciar ? 'Por Iniciar' : 'Terminadas'}
          </p>
          <span className="text-[11px] text-muted-foreground">
            {p.obras.length} {p.obras.length === 1 ? 'obra' : 'obras'}
          </span>
        </div>
      </div>
      <div className="flex-1 space-y-2 p-2">
        {p.obras.length === 0 && (
          <p className="px-1 py-6 text-center text-xs text-muted-foreground">
            {esPorIniciar ? 'Sin obras por iniciar.' : 'Sin obras terminadas.'}
          </p>
        )}
        {p.obras.map((o) => {
          const tc = o.tipo_casa;
          return (
            <Card key={o.codigo} className="space-y-2 p-2.5">
              <div className="flex items-center gap-2">
                <Buildings
                  className="h-4 w-4 shrink-0 text-muted-foreground"
                  weight="bold"
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate font-mono text-sm font-semibold">{o.codigo}</p>
                    <BadgeVenta estado={o.estado_venta} />
                  </div>
                  <p className="truncate text-[11px] text-muted-foreground">{tc ?? 'Sin tipo'}</p>
                </div>
              </div>
              {esPorIniciar ? (
                <Button
                  size="sm"
                  className="w-full bg-adelante-primary text-adelante-foreground hover:bg-adelante-primary/90"
                  disabled={p.enviando || p.bloqueado || !tc}
                  onClick={() => p.onAccion(o.codigo)}
                >
                  <Play className="mr-1.5 h-3.5 w-3.5" weight="fill" aria-hidden="true" />
                  Iniciar → Sprint {o.sprint_actual || 1}
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  disabled={p.enviando}
                  onClick={() => p.onAccion(o.codigo)}
                >
                  <ArrowUUpLeft className="mr-1.5 h-3.5 w-3.5" weight="bold" aria-hidden="true" />
                  Devolver
                </Button>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ===========================================================================
// Tarjeta
// ===========================================================================

interface TarjetaProps {
  obra: PreviewObra;
  expandida: boolean;
  arrastrando: boolean;
  enviando: boolean;
  bloqueado: boolean;
  /** El sprint de la tarjeta es "de espera" (colado/curado) → color distinto. */
  esEspera: boolean;
  /** Puede moverse hacia arriba en su columna (no es la primera). */
  puedeSubir: boolean;
  /** Puede moverse hacia abajo en su columna (no es la última). */
  puedeBajar: boolean;
  onSubir: () => void;
  onBajar: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onToggle: () => void;
  onCapturar: () => void;
  onAvanzar: () => void;
  /** Finalizar la obra (último sprint): pasa a 'finalizada' → Terminadas. */
  onTerminar: () => void;
  onRetroceder: () => void;
  onCongelarPedir: () => void;
  onDescongelar: () => void;
  onMarcarCompleta: (sub: PreviewSubPartida) => void;
  onPedirNC: (sub: PreviewSubPartida) => void;
  onPedirPct: (sub: PreviewSubPartida) => void;
}

function TarjetaObra(p: TarjetaProps) {
  const { obra } = p;
  const abrirCamara = useCamaraFotos();
  // Calidad: solo se consulta con la tarjeta expandida (no carga el tablero).
  const calidad = useControlesCalidad(
    p.expandida && obra.estado !== 'en_espera' ? obra.codigo : undefined,
  );
  // Modelo abreviado: las 2 primeras palabras del nombre largo del modelo
  // (ej. "Stella A 3D 3.5B WC (PA)" → "Stella A"). Se muestra como línea
  // principal de la tarjeta, con el tipo de casa al lado.
  const modeloCorto = obra.nombre_modelo?.trim().split(/\s+/).slice(0, 2).join(' ') || null;
  const congelada = obra.estado === 'en_espera';
  const pendientes = obra.pendientes_no_completadas;
  const criticas = obra.subs.filter((s) => s.es_critica);
  const noCriticas = obra.subs.filter((s) => !s.es_critica);
  const puedeRetroceder = puedeRetrocederObra(obra);
  const puedeAvanzar = obra.criticas_total === 0 || obra.criticas_completas >= obra.criticas_total;
  // Último sprint (no hay siguiente) → en vez de "Avanzar" se ofrece "Terminar"
  // (finaliza la obra). Solo si TODAS las sub-partidas están completas: las del
  // sprint (pendientes_no_completadas === 0) Y las arrastradas. El backend valida
  // igual (candado de finalización sobre todos los sprints).
  const esUltimoSprint = obra.sprint_siguiente == null;
  const arrastradasCompletas = obra.arrastradas.every(
    (s) => s.completada || s.pct_completado >= 100,
  );
  const puedeTerminar = obra.pendientes_no_completadas === 0 && arrastradasCompletas;
  // Adelantada respecto al plan/línea base (sprint_actual > sprint_plan).
  const adelantada = obra.sprint_plan != null && obra.sprint_actual > obra.sprint_plan;

  // Las de espera pueden moverse/avanzar aunque no se haya fijado la línea base
  // (no hay avance que capturar; el avance es manual cuando termina la espera).
  const arrastrable = !congelada && (!p.bloqueado || p.esEspera);

  return (
    <div
      draggable={arrastrable}
      onDragStart={(e) => {
        if (!arrastrable) return;
        e.dataTransfer.setData('text/plain', obra.codigo);
        e.dataTransfer.effectAllowed = 'move';
        p.onDragStart();
      }}
      onDragEnd={p.onDragEnd}
      className={cn(
        'rounded-md border bg-white transition-shadow',
        congelada
          ? 'border-zinc-300 bg-zinc-100 opacity-70'
          : p.esEspera
            ? 'border-violet-400 bg-violet-50'
            : adelantada
              ? 'border-indigo-400 bg-indigo-50'
              : obra.va_a_avanzar
                ? 'border-adelante-primary'
                : 'border-border',
        p.expandida && 'shadow-md',
        p.arrastrando && 'opacity-40',
        arrastrable && 'cursor-grab active:cursor-grabbing',
      )}
    >
      <div className="flex items-stretch">
        <button
          type="button"
          onClick={p.onToggle}
          className="flex min-w-0 flex-1 items-center justify-between gap-2 p-2.5 text-left"
        >
          <div className="flex min-w-0 items-center gap-2">
            <div
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
                congelada ? 'bg-zinc-200' : 'bg-adelante-primary/15',
              )}
            >
              <Buildings
                className={cn(
                  'h-4 w-4',
                  congelada ? 'text-zinc-500' : 'text-adelante-primary-dark',
                )}
                weight="bold"
                aria-hidden="true"
              />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <p
                  className={cn(
                    'truncate font-mono text-xs font-semibold',
                    congelada && 'text-muted-foreground',
                  )}
                >
                  {obra.codigo}
                </p>
                <BadgeVenta estado={obra.estado_venta} />
              </div>
              <p className="truncate text-[11px]">
                {modeloCorto ? (
                  <>
                    <span
                      className={cn(
                        'font-medium',
                        congelada ? 'text-muted-foreground' : 'text-foreground/80',
                      )}
                    >
                      {modeloCorto}
                    </span>
                    <span className="text-muted-foreground"> · {obra.tipo_casa}</span>
                  </>
                ) : (
                  <span className="text-muted-foreground">{obra.tipo_casa}</span>
                )}
              </p>
            </div>
          </div>
          {congelada ? (
            <Badge variant="secondary" className="shrink-0">
              <Pause className="mr-1 h-3 w-3" weight="bold" aria-hidden="true" />
              Congelada
            </Badge>
          ) : obra.va_a_avanzar && obra.sprint_siguiente != null ? (
            <ArrowCircleRight
              className="h-4 w-4 shrink-0 text-adelante-primary-dark"
              weight="fill"
              aria-label={`Avanzará a sprint ${obra.sprint_siguiente}`}
            />
          ) : (
            <CaretRight
              className={cn(
                'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                p.expandida && 'rotate-90',
              )}
              weight="bold"
              aria-hidden="true"
            />
          )}
        </button>
        {(p.puedeSubir || p.puedeBajar) && (
          <div className="flex shrink-0 flex-col items-center justify-center gap-0.5 border-l border-border px-1">
            <button
              type="button"
              onClick={p.onSubir}
              disabled={!p.puedeSubir || p.enviando}
              title="Subir prioridad"
              aria-label="Subir prioridad"
              className="rounded p-0.5 text-muted-foreground hover:bg-zinc-100 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
            >
              <CaretUp className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={p.onBajar}
              disabled={!p.puedeBajar || p.enviando}
              title="Bajar prioridad"
              aria-label="Bajar prioridad"
              className="rounded p-0.5 text-muted-foreground hover:bg-zinc-100 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
            >
              <CaretDown className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>

      {/* Stats compactos */}
      {!congelada && (
        <div className="grid grid-cols-2 gap-1 px-2.5 pb-2.5 text-[11px]">
          <Stat label="Críticas" value={`${obra.criticas_completas}/${obra.criticas_total}`} />
          <Stat label="Pendientes" value={pendientes} />
        </div>
      )}

      {/* Tarjeta congelada → solo botón Play para reactivar */}
      {congelada && p.expandida && (
        <div className="space-y-2 border-t border-border bg-white/60 p-2.5">
          {obra.motivo_inactiva && (
            <p className="text-[11px] text-muted-foreground">
              <span className="font-semibold">Motivo:</span> {obra.motivo_inactiva}
            </p>
          )}
          <button
            type="button"
            onClick={p.onDescongelar}
            disabled={p.enviando}
            className="flex w-full items-center justify-center gap-1 rounded-md border border-adelante-primary bg-adelante-primary py-1.5 text-xs font-semibold text-adelante-foreground hover:bg-adelante-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Play className="h-3.5 w-3.5" weight="fill" aria-hidden="true" />
            Reactivar obra
          </button>
        </div>
      )}

      {/* Tarjeta en ejecución expandida → subs + acciones */}
      {!congelada && p.expandida && (
        <div className="space-y-2 border-t border-border bg-zinc-50/60 p-2.5">
          {criticas.length > 0 && (
            <ListaSubs
              titulo="● Críticas"
              subs={criticas}
              acento="critico"
              enviando={p.enviando || p.bloqueado}
              onCompleta={p.onMarcarCompleta}
              onNC={p.onPedirNC}
              onPct={p.onPedirPct}
              calidad={calidad}
              onFoto={(s) =>
                abrirCamara({
                  obra: obra.codigo,
                  sub_partida_id: s.sub_partida_id,
                  etiqueta: `${obra.codigo} · ${s.nombre}`,
                })
              }
            />
          )}
          {noCriticas.length > 0 && (
            <ListaSubs
              titulo="○ No críticas"
              subs={noCriticas}
              acento="normal"
              enviando={p.enviando || p.bloqueado}
              onCompleta={p.onMarcarCompleta}
              onNC={p.onPedirNC}
              onPct={p.onPedirPct}
              calidad={calidad}
              onFoto={(s) =>
                abrirCamara({
                  obra: obra.codigo,
                  sub_partida_id: s.sub_partida_id,
                  etiqueta: `${obra.codigo} · ${s.nombre}`,
                })
              }
            />
          )}

          {/* Fotos de la obra (sin sub-partida) y su galería */}
          <div className="grid grid-cols-2 gap-1">
            <button
              type="button"
              onClick={() => abrirCamara({ obra: obra.codigo, etiqueta: obra.codigo })}
              className="flex items-center justify-center gap-1 rounded-md border border-border bg-white py-1.5 text-xs font-medium hover:bg-zinc-50"
            >
              <Camera className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
              Tomar fotos
            </button>
            <Link
              to={`/campo/fotos/${encodeURIComponent(obra.codigo)}`}
              className="flex items-center justify-center gap-1 rounded-md border border-border bg-white py-1.5 text-xs font-medium hover:bg-zinc-50"
            >
              <Images className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
              Ver fotos
            </Link>
          </div>

          <button
            type="button"
            onClick={p.onCapturar}
            className="flex w-full items-center justify-center gap-1 rounded-md border border-border bg-white py-1.5 text-xs font-medium hover:bg-zinc-50"
          >
            Ir a captura completa
            <CaretRight className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
          </button>

          {/* Acciones de sprint */}
          <div className="grid grid-cols-3 gap-1">
            <button
              type="button"
              onClick={p.onRetroceder}
              // Retroceder = DESHACER un movimiento manual; NO depende de la
              // línea base (no captura avance). Por eso no se bloquea con
              // `p.bloqueado` — el backend igual valida (anterior cerrado y no
              // de espera → 409).
              disabled={!puedeRetroceder || p.enviando}
              title={
                puedeRetroceder
                  ? 'Volver al sprint anterior'
                  : 'No se puede: el sprint anterior ya fue cerrado'
              }
              className="flex items-center justify-center gap-1 rounded-md border border-border bg-white py-1.5 text-[11px] font-medium text-foreground hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
            >
              <ArrowCircleLeft className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
              Atrás
            </button>
            <button
              type="button"
              onClick={p.onCongelarPedir}
              disabled={p.enviando}
              title="Congelar (estado: en espera)"
              className="flex items-center justify-center gap-1 rounded-md border border-amber-200 bg-amber-50 py-1.5 text-[11px] font-medium text-amber-700 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Pause className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
              Congelar
            </button>
            {esUltimoSprint ? (
              <button
                type="button"
                onClick={p.onTerminar}
                disabled={!puedeTerminar || p.enviando}
                title={
                  puedeTerminar
                    ? 'Terminar obra → pasa a Terminadas'
                    : 'No se puede terminar: faltan sub-partidas por completar (incluí las arrastradas)'
                }
                className="flex items-center justify-center gap-1 rounded-md border border-emerald-500 bg-emerald-500 py-1.5 text-[11px] font-semibold text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:border-border disabled:bg-zinc-100 disabled:text-zinc-400"
              >
                <CheckCircle className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
                Terminar
              </button>
            ) : (
              <button
                type="button"
                onClick={p.onAvanzar}
                disabled={!puedeAvanzar || p.enviando || (p.bloqueado && !p.esEspera)}
                title={
                  puedeAvanzar
                    ? 'Avanzar al siguiente sprint'
                    : 'No se puede avanzar: hay críticas sin completar'
                }
                className="flex items-center justify-center gap-1 rounded-md border border-adelante-primary bg-adelante-primary py-1.5 text-[11px] font-semibold text-adelante-foreground hover:bg-adelante-primary-dark disabled:cursor-not-allowed disabled:border-border disabled:bg-zinc-100 disabled:text-zinc-400"
              >
                Avanzar
                <ArrowCircleRight className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded bg-zinc-50 px-1.5 py-0.5">
      <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-semibold tabular-nums">{value}</p>
    </div>
  );
}

interface ListaProps {
  titulo: string;
  subs: PreviewSubPartida[];
  acento: 'critico' | 'normal';
  enviando: boolean;
  onCompleta: (sub: PreviewSubPartida) => void;
  onNC: (sub: PreviewSubPartida) => void;
  onPct: (sub: PreviewSubPartida) => void;
  /** Controles de calidad de la obra (solo en la tarjeta expandida). */
  calidad?: ReturnType<typeof useControlesCalidad>;
  /** Tomar fotos ligadas a la sub-partida. */
  onFoto?: (sub: PreviewSubPartida) => void;
}

function ListaSubs({
  titulo,
  subs,
  acento,
  enviando,
  onCompleta,
  onNC,
  onPct,
  calidad,
  onFoto,
}: ListaProps) {
  return (
    <div>
      {titulo && (
        <p
          className={cn(
            'mb-1 text-[10px] font-semibold uppercase tracking-wider',
            acento === 'critico' ? 'text-red-700' : 'text-muted-foreground',
          )}
        >
          {titulo}
        </p>
      )}
      <ul className="space-y-1 text-[11px]">
        {subs.map((s) => {
          const completa = s.completada || s.pct_completado >= 100;
          const tieneNC = !!s.nc_causa && !completa;
          const pct = Math.round(s.pct_completado);
          const piso = Math.round(s.piso_pct);
          // Si el piso es 100% (un cierre la fijó completa), no se puede revertir.
          const bloqueadaPorCierre = completa && piso >= 100;
          const control = calidad?.controles.get(s.sub_partida_id);
          // Calidad: según la ficha, sin inspección aprobada no se inicia (% y ✓)
          // o no se completa (✓).
          const bloqueo = bloqueoCalidad(control, s.pct_completado);
          return (
            <li key={s.sub_partida_id} className="rounded bg-white p-1.5">
              <div className="flex items-center gap-1.5">
                <span className="min-w-0 flex-1 truncate font-medium">{s.nombre}</span>
                {onFoto && (
                  <button
                    type="button"
                    onClick={() => onFoto(s)}
                    title="Tomar fotos de esta sub-partida"
                    aria-label={`Tomar fotos de ${s.nombre}`}
                    className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-zinc-100 hover:text-foreground"
                  >
                    <Camera className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
                  </button>
                )}
                {/* % y NC son independientes: se muestran ambos si aplican. */}
                {tieneNC && (
                  <span className="shrink-0 rounded bg-red-100 px-1 text-[9px] font-semibold uppercase text-red-700">
                    NC
                  </span>
                )}
                <span
                  className={cn(
                    'shrink-0 tabular-nums',
                    completa ? 'font-semibold text-adelante-primary-dark' : 'text-muted-foreground',
                  )}
                >
                  {pct}%
                </span>
              </div>
              <div className="mt-1 grid grid-cols-3 gap-1">
                <button
                  type="button"
                  onClick={() => onPct(s)}
                  disabled={enviando || bloqueo.inicio}
                  title={
                    bloqueo.inicio
                      ? 'Requiere la inspección de calidad aprobada'
                      : 'Establecer % parcial'
                  }
                  className={cn(
                    'flex items-center justify-center gap-0.5 rounded border py-1 text-[10px] disabled:cursor-not-allowed disabled:opacity-50',
                    pct > 0 && !completa
                      ? 'border-blue-300 bg-blue-50 text-blue-700'
                      : 'border-border bg-white hover:bg-zinc-50',
                  )}
                >
                  <Percent className="h-3 w-3" weight="bold" aria-hidden="true" />
                  {pct > 0 && !completa ? pct : ''}
                </button>
                <button
                  type="button"
                  onClick={() => onNC(s)}
                  disabled={enviando || completa}
                  title={
                    completa
                      ? 'Una sub-partida al 100% no puede ser NC'
                      : tieneNC
                        ? 'Ya tiene NC — tocá para cambiar la causa'
                        : 'Marcar no cumplido'
                  }
                  className={cn(
                    'flex items-center justify-center gap-0.5 rounded border py-1 text-[10px] disabled:cursor-not-allowed disabled:border-border disabled:text-zinc-300 disabled:hover:bg-white',
                    tieneNC
                      ? 'border-red-400 bg-red-500 text-white'
                      : 'border-red-200 bg-white text-red-700 hover:bg-red-50',
                  )}
                >
                  <XCircle className="h-3 w-3" weight="bold" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => onCompleta(s)}
                  disabled={enviando || bloqueadaPorCierre || (bloqueo.completar && !completa)}
                  title={
                    bloqueo.completar && !completa
                      ? 'Requiere la inspección de calidad aprobada'
                      : bloqueadaPorCierre
                        ? 'Fijada al 100% por un cierre — no se puede revertir'
                        : completa
                          ? `Tocá de nuevo para revertir${piso > 0 ? ` al piso ${piso}%` : ' a 0%'}`
                          : 'Marcar completada (100%)'
                  }
                  className={cn(
                    'flex items-center justify-center gap-0.5 rounded border py-1 text-[10px] disabled:cursor-not-allowed disabled:opacity-50',
                    completa
                      ? 'border-adelante-primary bg-adelante-primary text-adelante-foreground'
                      : 'border-border bg-white hover:bg-zinc-50',
                  )}
                >
                  <Check className="h-3 w-3" weight="bold" aria-hidden="true" />
                </button>
              </div>
              {control && (control.inspeccion || control.evaluacion) && calidad && (
                <ControlCalidadFila
                  control={control}
                  bloqueo={bloqueo}
                  disabled={calidad.abriendo}
                  onAbrir={(tipo) => calidad.abrir(s.sub_partida_id, tipo)}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
